import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CheckIcon,
  DownloadIcon,
  Stack,
  SwapHorizIcon,
  Text,
  UploadFileIcon,
} from '@billda/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useSiteReimportStore } from '../../../stores/siteReimportStore';
import { exportSiteCsv } from '../../../api/sitesApi';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { useSnackbarStore } from '../../../../../shared/stores/snackbarStore';
import { sitesKeys } from '../../../hooks/useSites';
import { plotsKeys } from '../../../hooks/usePlotsQuery';
import { labourItemsKeys } from '../../../hooks/useLabourItemsQuery';
import ReimportSummaryTiles from '../../reimport/ReimportSummaryTiles';
import ReimportMissingLabourItemsCreator from '../../reimport/ReimportMissingLabourItemsCreator';
import ReimportDiffTable, {
  countEffectiveChanges,
} from '../../reimport/table/ReimportDiffTable';
import ConfirmDialog from '../../../../../shared/components/ui/ConfirmDialog';

interface SiteReimportPanelProps {
  siteId: number;
  onClose?: () => void;
}

export function SiteReimportPanel({ siteId, onClose }: SiteReimportPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { closePanel } = useSitePanelState();
  const handleClose = onClose ?? closePanel;
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const [exporting, setExporting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  const {
    selectedFile,
    setSelectedFile,
    validateCsvFile,
    previewData,
    loading,
    error,
    uploadAndPreview,
    clearError,
    executing,
    executeReimport,
    resetAll,
    createdItems,
    clearLabourItemCreationState,
  } = useSiteReimportStore();

  // Reset store state when panel unmounts (X button, ESC, switching panels)
  useEffect(() => {
    return () => {
      useSiteReimportStore.getState().resetAll();
      useSiteReimportStore.getState().clearError();
      useSiteReimportStore.getState().clearLabourItemCreationState();
    };
  }, []);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateCsvFile(file);
    if (validationError) {
      showSnackbar(validationError, 'error');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
    clearError();

    try {
      await uploadAndPreview(siteId, file);
      showSnackbar(`File "${file.name}" uploaded successfully!`);
    } catch {
      // Error handled by store
    }
  };

  const handleReplaceFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await exportSiteCsv(siteId);
      showSnackbar('CSV exported successfully!');
    } catch {
      showSnackbar('Failed to export CSV', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleApplyChanges = async () => {
    if (!previewData || !selectedFile || executing) return;

    try {
      const result = await executeReimport(siteId, selectedFile);
      if (result) {
        // Build success message
        const parts: string[] = ['Re-import complete.'];
        const changes: string[] = [];
        if (result.plotsAdded > 0)
          changes.push(`${result.plotsAdded} plots added`);
        if (result.plotsRemoved > 0)
          changes.push(`${result.plotsRemoved} plots removed`);
        if (result.labourItemsAdded > 0)
          changes.push(`${result.labourItemsAdded} items added`);
        if (result.labourItemsRemoved > 0)
          changes.push(`${result.labourItemsRemoved} items removed`);
        if (result.labourItemsPriceChanged > 0)
          changes.push(`${result.labourItemsPriceChanged} pricing updates`);
        if (changes.length > 0) parts.push(changes.join(', ') + '.');
        if (result.protectedItemsSkipped > 0) {
          parts.push(
            `${result.protectedItemsSkipped} protected item${result.protectedItemsSkipped !== 1 ? 's' : ''} skipped.`
          );
        }

        // Set success message in store (persists after panel close)
        useSiteReimportStore.setState({
          successMessage: parts.join(' '),
        });
        resetAll();

        // Invalidate caches and close panel
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: sitesKeys.all }),
          queryClient.invalidateQueries({ queryKey: plotsKeys.all }),
          queryClient.invalidateQueries({
            queryKey: labourItemsKeys.all,
          }),
        ]);

        handleClose();
      }
    } catch {
      // Error handled by store
    }
  };

  const handleCancel = () => {
    if (previewData || selectedFile) {
      setShowConfirm(true);
    } else {
      resetAll();
      clearLabourItemCreationState();
      handleClose();
    }
  };

  const handleConfirmCancel = () => {
    resetAll();
    clearError();
    clearLabourItemCreationState();
    setShowConfirm(false);
    handleClose();
  };

  const hasUnresolvedMissingItems =
    previewData?.missingLabourDefinitions?.some(
      item => !createdItems.has(item)
    ) || false;

  const effectiveChangeCount = previewData
    ? countEffectiveChanges(previewData.plots)
    : 0;
  const hasChanges = effectiveChangeCount > 0;
  const skippedItemCount = previewData?.summary.protectedItemsSkipped ?? 0;

  const canApply = Boolean(
    previewData &&
      selectedFile &&
      hasChanges &&
      !executing &&
      !loading &&
      !hasUnresolvedMissingItems
  );

  return (
    <div className="flex h-full flex-col" data-testid="site-reimport-panel">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <Stack spacing={3}>
          {/* Upload Zone */}
          <div data-testid="reimport-panel-upload">
            {!selectedFile || loading ? (
              // No file uploaded — show upload prompt
              <div className="rounded-xl border-2 border-dashed border-border-default bg-surface-raised p-6 text-center">
                <UploadFileIcon
                  className="mb-2 size-10 text-content-secondary"
                  aria-hidden="true"
                />
                <Text variant="body1" className="mb-2">
                  Upload a CSV file to preview changes
                </Text>
                <Text variant="body2" color="secondary" className="mb-4">
                  Use the exported CSV format. Its CONFIGURATION row preserves
                  fixed amounts and quantity rates.
                </Text>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  className="flex-wrap"
                >
                  <Button
                    variant="neutral"
                    loading={loading}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    data-testid="reimport-file-upload-button"
                  >
                    {loading ? null : (
                      <UploadFileIcon className="size-4" aria-hidden="true" />
                    )}
                    {loading ? 'Processing...' : 'Select CSV File'}
                  </Button>
                  <Button
                    variant="ghost"
                    loading={exporting}
                    onClick={handleExportCsv}
                    disabled={exporting}
                    data-testid="reimport-download-csv-button"
                  >
                    {exporting ? null : (
                      <DownloadIcon className="size-4" aria-hidden="true" />
                    )}
                    {exporting ? 'Exporting...' : 'Download Current CSV'}
                  </Button>
                </Stack>
              </div>
            ) : (
              // File uploaded — show filename with replace option
              <div className="flex items-center gap-4 rounded-lg border border-border-default bg-surface-raised p-4">
                <UploadFileIcon
                  className="size-5 text-content-accent"
                  aria-hidden="true"
                />
                <div className="flex-1">
                  <Text variant="body2" className="font-medium">
                    {selectedFile.name}
                  </Text>
                  <Text variant="caption" color="secondary">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Text>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReplaceFile}
                  disabled={loading}
                >
                  <SwapHorizIcon className="size-4" aria-hidden="true" />
                  Replace
                </Button>
              </div>
            )}

            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              ref={fileInputRef}
              className="hidden"
              data-testid="reimport-file-input"
            />
          </div>

          {/* Error */}
          {error && (
            <Alert severity="error" data-testid="reimport-panel-error">
              {error}
            </Alert>
          )}

          {/* Change Summary — appears after upload */}
          {previewData && (
            <>
              {hasChanges && (
                <div data-testid="reimport-panel-summary">
                  <Text variant="body1" className="mb-2 font-medium">
                    Change Summary
                  </Text>
                  <ReimportSummaryTiles summary={previewData.summary} />
                </div>
              )}

              {/* Missing Labour Items — appears if needed */}
              {previewData.missingLabourDefinitions &&
                previewData.missingLabourDefinitions.length > 0 && (
                  <ReimportMissingLabourItemsCreator
                    siteId={siteId}
                    missingItems={previewData.missingLabourDefinitions}
                  />
                )}

              {/* Change Details */}
              <ReimportDiffTable plots={previewData.plots} />
            </>
          )}
        </Stack>
      </div>

      {/* Sticky Confirm Bar */}
      <div
        className="flex justify-end gap-4 border-t border-border-default bg-surface-default p-4"
        data-testid="reimport-panel-actions"
      >
        {previewData && (
          <Text
            variant="body2"
            color="secondary"
            className="mr-auto self-center"
          >
            {hasChanges
              ? `${effectiveChangeCount} change${effectiveChangeCount === 1 ? '' : 's'} will apply${skippedItemCount > 0 ? ` · ${skippedItemCount} skipped` : ''}`
              : skippedItemCount > 0
                ? `${skippedItemCount} requested change${skippedItemCount === 1 ? '' : 's'} skipped`
                : ''}
          </Text>
        )}
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={executing}
          data-testid="reimport-cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          loading={executing}
          onClick={() => setShowApplyConfirm(true)}
          disabled={!canApply}
          data-testid="reimport-confirm-button"
        >
          {executing ? null : (
            <CheckIcon className="size-4" aria-hidden="true" />
          )}
          {executing ? 'Applying...' : 'Apply Changes'}
        </Button>
      </div>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        open={showConfirm}
        title="Cancel Re-Import"
        message="Are you sure you want to cancel? All progress including the uploaded file and preview data will be lost."
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowConfirm(false)}
        confirmText="Yes, Cancel"
        cancelText="Continue"
      />
      <ConfirmDialog
        open={showApplyConfirm}
        title="Apply re-import changes?"
        message={`${effectiveChangeCount} change${effectiveChangeCount === 1 ? '' : 's'} will be applied${skippedItemCount > 0 ? ` and ${skippedItemCount} requested change${skippedItemCount === 1 ? '' : 's'} will be skipped` : ''}. This cannot be undone.`}
        onConfirm={() => {
          setShowApplyConfirm(false);
          void handleApplyChanges();
        }}
        onCancel={() => setShowApplyConfirm(false)}
        confirmText="Apply Changes"
        cancelText="Review Again"
      />
    </div>
  );
}
